<script lang="ts">
	import { clone, type JsonValue } from "@bufbuild/protobuf";
	import { Input, Label, Radio, Select } from "flowbite-svelte-v1";
	import { PageSchema, type Page } from "#gen/pages/v2/page_pb.js";
	import P from "#lib/components/P.svelte";
	import ColorInputV2 from "#lib/components/flow/editor/appearance/ColorInputV2.svelte";
	import { BORDER_RADIUS_VALUES, HORIZONTAL_ALIGNMENTS, IMAGE_BUTTON_DEFAULT, TEXT_SIZES, VERTICAL_ALIGNMENTS } from "#lib/components/flow/editor/appearance/constants.js";
	import FeatureFlagGate from "#lib/permissions/FeatureFlagGate.svelte";
	import type { JsonObject } from "#lib/protobuf-compat.js";
	import { booleanValue, numberValue, setBooleanValue, setNumberValue, setStringValue, setStructValue, stringValue, structValue } from "#lib/struct.js";
	import type { Writable } from "svelte/store";

	interface Props {
		id: string;
		page: Writable<Page>;
		onChange: (value?: JsonValue) => void;
	}

	let { id, page, onChange }: Props = $props();

	const textAlignment = $derived(stringValue(structValue($page.config, id), "textAlignment") ?? IMAGE_BUTTON_DEFAULT.textAlignment);
	const textSize = $derived(stringValue(structValue($page.config, id), "textSize") ?? IMAGE_BUTTON_DEFAULT.textSize);
	const verticalAlignment = $derived(stringValue(structValue($page.config, id), "verticalAlignment") ?? IMAGE_BUTTON_DEFAULT.verticalAlignment);
	const textBoxWidth = $derived(numberValue(structValue($page.config, id), "textBoxWidth") ?? IMAGE_BUTTON_DEFAULT.textBoxWidth);
	const borderRadius = $derived(stringValue(structValue($page.config, id), "borderRadius") ?? IMAGE_BUTTON_DEFAULT.borderRadius);
	const backgroundColor = $derived(stringValue(structValue($page.config, id), "backgroundColor") ?? IMAGE_BUTTON_DEFAULT.backgroundColor);
	const overlayColor = $derived(stringValue(structValue($page.config, id), "overlayColor") ?? IMAGE_BUTTON_DEFAULT.overlayColor);
	const borderColor = $derived(stringValue(structValue($page.config, id), "borderColor") ?? IMAGE_BUTTON_DEFAULT.borderColor);
	const textColor = $derived(stringValue(structValue($page.config, id), "textColor") ?? IMAGE_BUTTON_DEFAULT.textColor);
	const textShouldOverlay = $derived(booleanValue(structValue($page.config, id), "textShouldOverlay") ?? IMAGE_BUTTON_DEFAULT.textShouldOverlay);

	function getConfig(): JsonObject {
		let config = structValue($page.config, id);
		if (!config) {
			config = {} as JsonObject;
			setStructValue($page.config, id, config);
		}
		return config;
	}

	function handleChangeAppearance(e: Event, key: string) {
		const config = getConfig();
		const value = (e.target as HTMLSelectElement).value;
		if (value) {
			setStringValue(config, key, value);
		} else {
			delete config[key];
		}
		$page = $page;
		onChange?.();
	}

	function onColorInput(key: string, value: string) {
		const config = getConfig();
		if (value) {
			setStringValue(config, key, value);
			$page = clone(PageSchema, $page);
		}
	}

	function handleTextBoxWidthChange(e: Event) {
		const parsed = Number((e.target as HTMLInputElement).value);
		const value = Number.isFinite(parsed) ? Math.min(100, Math.max(1, Math.round(parsed))) : IMAGE_BUTTON_DEFAULT.textBoxWidth;
		const config = getConfig();
		setNumberValue(config, "textBoxWidth", value);
		$page = $page;
		onChange?.();
	}

	function handleTextOverlayChanged(e: Event) {
		const target = e.target as HTMLInputElement;
		const value = target.value === "true";
		const config = getConfig();
		if (booleanValue(config, "textShouldOverlay") === value) {
			return;
		}
		setBooleanValue(config, "textShouldOverlay", value);
		$page = clone(PageSchema, $page);
		onChange?.(value);
	}
</script>

<div class="flex flex-col gap-5">
	<P class="text-sm font-bold text-black">Appearance</P>
	<FeatureFlagGate flag="image_button_text_overlay">
		<div class="flex flex-col gap-2">
			<P class="text-xs font-semibold tracking-wide text-gray-500 uppercase">Card presentation</P>
			<div class="flex flex-col gap-2">
				<div class="flex flex-col border-l-[5px] border-l-gray-300 pl-6">
					<Radio name="{id}-textShouldOverlay" value="false" group={textShouldOverlay ? "true" : "false"} onchange={handleTextOverlayChanged}>Stacked</Radio>
					<Radio name="{id}-textShouldOverlay" value="true" group={textShouldOverlay ? "true" : "false"} onchange={handleTextOverlayChanged}>Overlay</Radio>
				</div>
			</div>
		</div>
	</FeatureFlagGate>
	{#if textShouldOverlay}
		<div class="flex flex-col gap-2">
			<P class="text-xs font-semibold tracking-wide text-gray-500 uppercase">Placement</P>
			<div class="grid grid-cols-3 gap-2 border-l-[5px] border-l-gray-300 pl-6">
				<div class="flex grow flex-col gap-1">
					<Label for="{id}-alignment-label" class="font-medium text-black">Text alignment</Label>
					<Select classes={{ select: "truncate" }} items={HORIZONTAL_ALIGNMENTS} value={textAlignment} aria-labelledby="{id}-alignment-label" onchange={e => handleChangeAppearance(e, "textAlignment")} />
				</div>
				<div class="flex grow flex-col gap-1">
					<Label for="{id}-vert-alignment-label" class="font-medium text-black">Vertical alignment</Label>
					<Select classes={{ select: "truncate" }} items={VERTICAL_ALIGNMENTS} value={verticalAlignment} aria-labelledby="{id}-vert-alignment-label" onchange={e => handleChangeAppearance(e, "verticalAlignment")} />
				</div>
				<div class="flex grow flex-col gap-1">
					<Label for="{id}-text-box-width-label" class="font-medium text-black">Text box width</Label>
					<div class="relative w-full">
						<Input id="{id}-text-box-width" type="number" min={1} max={100} step={1} class="w-full [appearance:textfield] pr-11 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none" value={textBoxWidth} aria-labelledby="{id}-text-box-width-label" onchange={handleTextBoxWidthChange} />
						<span class="pointer-events-none absolute inset-y-px right-px flex items-center rounded-r-lg border-l border-gray-300 px-2.5 py-2 text-sm text-gray-500">%</span>
					</div>
				</div>
			</div>
		</div>
		<div class="flex flex-col gap-2">
			<P class="text-xs font-semibold tracking-wide text-gray-500 uppercase">Text</P>
			<div class="grid grid-cols-3 gap-2 border-l-[5px] border-l-gray-300 pl-6">
				<div class="flex grow flex-col gap-1">
					<Label for="{id}-text-size" class="font-medium text-black">Text size</Label>
					<Select classes={{ select: "truncate" }} items={TEXT_SIZES} value={textSize} aria-labelledby="{id}-text-size" onchange={e => handleChangeAppearance(e, "textSize")} />
				</div>
				<div class="flex grow flex-col gap-1">
					<Label for="{id}-color-label" class="font-medium text-black">Text color</Label>
					<ColorInputV2 id="{id}-color-label" resetColor="#ffffff" display="inline" value={textColor} showBrandKit={false} oninput={value => onColorInput("textColor", value)} onchange={value => onChange?.(value)} />
				</div>
				<div class="flex grow flex-col gap-1">
					<Label for="{id}-text-background-color" class="font-medium text-black">Text background color</Label>
					<ColorInputV2 id="{id}-text-background-color" allowTransparent resetColor={IMAGE_BUTTON_DEFAULT.backgroundColor} display="inline" value={backgroundColor} showBrandKit={false} oninput={value => onColorInput("backgroundColor", value)} onchange={value => onChange?.(value)} />
				</div>
			</div>
		</div>
		<div class="flex flex-col gap-2">
			<P class="text-xs font-semibold tracking-wide text-gray-500 uppercase">Card</P>
			<div class="grid grid-cols-3 gap-2 border-l-[5px] border-l-gray-300 pl-6">
				<div class="flex grow flex-col gap-1">
					<Label for="{id}-border-radius" class="font-medium text-black">Border radius</Label>
					<Select classes={{ select: "truncate" }} items={BORDER_RADIUS_VALUES} value={borderRadius} aria-labelledby="{id}-border-radius" onchange={e => handleChangeAppearance(e, "borderRadius")} />
				</div>
				<div class="flex grow flex-col gap-1">
					<Label for="{id}-border-color" class="font-medium text-black">Border color</Label>
					<ColorInputV2 id="{id}-border-color" resetColor="#000000" display="inline" value={borderColor} showBrandKit={false} oninput={value => onColorInput("borderColor", value)} onchange={value => onChange?.(value)} />
				</div>
				<div class="flex grow flex-col gap-1">
					<Label for="{id}-overlay-color" class="font-medium text-black">Overlay color</Label>
					<ColorInputV2 id="{id}-overlay-color" allowTransparent resetColor="#00000000" display="inline" value={overlayColor} showBrandKit={false} oninput={value => onColorInput("overlayColor", value)} onchange={value => onChange?.(value)} />
				</div>
			</div>
		</div>
	{:else}
		<div class="flex flex-col gap-2">
			<P class="text-xs font-semibold tracking-wide text-gray-500 uppercase">Card style</P>
			<div class="grid grid-cols-3 gap-2 border-l-[5px] border-l-gray-300 pl-6">
				<div class="flex grow flex-col gap-1">
					<Label for="{id}-border-radius" class="font-medium text-black">Border radius</Label>
					<Select classes={{ select: "truncate" }} items={BORDER_RADIUS_VALUES} value={borderRadius} aria-labelledby="{id}-border-radius" onchange={e => handleChangeAppearance(e, "borderRadius")} />
				</div>
				<div class="flex grow flex-col gap-1">
					<Label for="{id}-border-color" class="font-medium text-black">Border color</Label>
					<ColorInputV2 id="{id}-border-color" resetColor="#000000" display="inline" value={borderColor} showBrandKit={false} oninput={value => onColorInput("borderColor", value)} onchange={value => onChange?.(value)} />
				</div>
				<div class="flex grow flex-col gap-1">
					<Label for="{id}-background-color" class="font-medium text-black">Background color</Label>
					<ColorInputV2 id="{id}-background-color" allowTransparent resetColor={IMAGE_BUTTON_DEFAULT.backgroundColor} display="inline" value={backgroundColor} showBrandKit={false} oninput={value => onColorInput("backgroundColor", value)} onchange={value => onChange?.(value)} />
				</div>
				<div class="flex grow flex-col gap-1">
					<Label for="{id}-alignment-label" class="font-medium text-black">Text alignment</Label>
					<Select classes={{ select: "truncate" }} items={HORIZONTAL_ALIGNMENTS} value={textAlignment} aria-labelledby="{id}-alignment-label" onchange={e => handleChangeAppearance(e, "textAlignment")} />
				</div>
				<div class="flex grow flex-col gap-1">
					<Label for="{id}-text-size" class="font-medium text-black">Text size</Label>
					<Select classes={{ select: "truncate" }} items={TEXT_SIZES} value={textSize} aria-labelledby="{id}-text-size" onchange={e => handleChangeAppearance(e, "textSize")} />
				</div>
				<div class="flex grow flex-col gap-1">
					<Label for="{id}-color-label" class="font-medium text-black">Text color</Label>
					<ColorInputV2 id="{id}-color-label" resetColor="#ffffff" display="inline" value={textColor} showBrandKit={false} oninput={value => onColorInput("textColor", value)} onchange={value => onChange?.(value)} />
				</div>
			</div>
		</div>
	{/if}
</div>
